import { Typography, Badge } from '@lucid/lego';

export const formatCaseTitle = (caseId: string | null | undefined) => {
  if (!caseId) return 'xxxxxxxxxxxx';
  const shortId = caseId.slice(-12);
  return `${shortId}`;
};

export const getStatusBadgeColor = (status: string | null | undefined) => {
  if (!status) return 'neutral';
  switch (status.toLowerCase()) {
    case 'open':
      return 'success';
    case 'in-progress':
      return 'warning';
    case 'awaiting-input':
      return 'informative';
    case 'resolved':
      return 'success';
    case 'closed':
      return 'brand';
    case 'draft':
      return 'warning';
    default:
      return 'neutral';
  }
};

export const getPriorityBadgeColor = (priority: string | null | undefined) => {
  if (!priority) return 'neutral';
  switch (priority.toLowerCase()) {
    case 'urgent':
      return 'error';
    case 'escalated':
      return 'error';
    case 'high':
      return 'error';
    case 'standard':
      return 'informative';
    case 'medium':
      return 'informative';
    case 'low':
      return 'warning';
    case 'tt':
      return 'brand';
    default:
      return 'neutral';
  }
};

export const formatCaseSubCategory = (caseSubCategory: string | null | undefined) => {
  if (!caseSubCategory) return '';
  switch (caseSubCategory) {
    case 'BACK_OFFICE_SUPPORT':
      return 'Back Office Support';
    case 'PRODUCT_KNOWLEDGE':
      return 'Product Knowledge';
    case 'ESCALATION':
      return 'Escalation';
    case 'OTHERS':
      return 'Others';
    default:
      return caseSubCategory;
  }
};

export const formatStatus = (status: string | null | undefined) => {
  if (!status) return '';
  return status
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export interface Case {
  caseIdentifier?: number;
  customerName: string;
  vin: string;
  caseType: string;
  caseSubCategory: string;
  caseSubject: string;
  customerCaseIdentifier: string;
  createdDatetime: string;
  status: string;
  priority: string;
}

interface CreateColumnsParams {
  theme: 'light' | 'dark';
  onEditClick: (record: Case) => void;
}

export const createColumns = ({ theme, onEditClick }: CreateColumnsParams) => [
  {
    key: 'customerCaseIdentifier',
    title: 'Case Number',
    width: '140px',
    sortable: true,
    render: (_: unknown, record: Case) => (
      <Typography
        variant="body"
        size="small"
        theme={theme}
        color="blue"
        onClick={() => onEditClick(record)}
      >
        {formatCaseTitle(record.customerCaseIdentifier)}
      </Typography>
    ),
  },
  {
    key: 'customerName',
    title: 'Customer',
    width: '160px',
    sortable: true,
    render: (_: unknown, record: Case) => (
      <Typography variant="body" size="small" theme={theme} color="black">
        {record.customerName || '-'}
      </Typography>
    ),
  },
  {
    key: 'vin',
    title: 'VIN',
    width: '180px',
    sortable: true,
    render: (_: unknown, record: Case) => (
      <Typography variant="body" size="small" theme={theme} color="black">
        {record.vin || '-'}
      </Typography>
    ),
  },
  {
    key: 'caseSubject',
    title: 'Issue',
    width: '200px',
    sortable: true,
    render: (_: unknown, record: Case) => (
      <Typography variant="body" size="small" theme={theme} color="black">
        {record.caseSubject || '-'}
      </Typography>
    ),
  },
  {
    key: 'status',
    title: 'Status',
    width: '120px',
    sortable: true,
    render: (_: unknown, record: Case) => (
      <Badge
        label={formatStatus(record.status)}
        color={getStatusBadgeColor(record.status)}
        type="secondary"
        theme={theme}
      />
    ),
  },
  {
    key: 'priority',
    title: 'Priority',
    width: '120px',
    align: 'center',
    sortable: true,
    render: (_: unknown, record: Case) => {
      const priority = record.priority || 'standard';
      const formattedPriority = priority.charAt(0).toUpperCase() + priority.slice(1).toLowerCase();
      return (
        <Badge
          label={formattedPriority}
          color={getPriorityBadgeColor(priority)}
          type="secondary"
          theme={theme}
        />
      );
    },
  },
  {
    key: 'createdDatetime',
    title: 'Created',
    width: '140px',
    sortable: true,
    render: (_: unknown, record: Case) => {
      const date = new Date(record.createdDatetime);
      return (
        <Typography variant="body" size="small" theme={theme} color="black">
          {date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
          })}
        </Typography>
      );
    },
  },
];
